import type { HTMLAttributes, Ref } from "react";
import { createElement } from "react";
import { cn } from "@rosette/ts/src/util/cn.js";


/**
 * translucent darkens whatever is behind it, for text over the page's
 * backgrounds. solid is opaque, for dense data such as tables and lists
 * that a textured background would make hard to read.
 */
export type PanelTone = "translucent" | "solid";

export type PanelSize = "sm" | "md";

const TONE_CLASSES: Record<PanelTone, string> = {
    translucent: "bg-shade",
    solid: "border border-panel bg-page",
};

const SIZE_CLASSES: Record<PanelSize, string> = {
    sm: "rounded-xl px-3 py-2",
    md: "rounded-2xl p-3 md:p-4",
};


interface PanelProps extends HTMLAttributes<HTMLElement> {
    as?: "div" | "section" | "aside";
    tone?: PanelTone;
    size?: PanelSize;
    ref?: Ref<HTMLElement>;
}


/**
 * A flat area that groups content within the page. It sits on the
 * background rather than above it, unlike a Card.
 */
export function Panel({
    as = "div",
    tone = "translucent",
    size = "md",
    className,
    ...props
}: PanelProps) {
    return createElement(as, {
        ...props,
        className: cn(
            "w-full *:first:mt-0 *:last:mb-0",
            TONE_CLASSES[tone],
            SIZE_CLASSES[size],
            className ?? null,
        ),
    });
}
